import { Grid, Box, useTheme, useMediaQuery } from '@mui/material';
import { FC, Fragment, useEffect, useState } from 'react';
import { PrimaryButton } from '../../../../Buttons';
import LeadFormListTable from '../../../../Tables/LeadList/LeadFormListTable';
import { HtmlTooltip } from '../../../../Tooltip';
import { Brand, FBLeadGenData, LeadFormData } from '../../../../../types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';
import {
  archiveLeadForm,
  getBrandLeadForms,
  syncLeadForms,
} from '../../../../../services/lead';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import PopupModal from '../../../../Modal';
import ModalHeader from '../../../../Modal/ModalHeader';
import FacebookLeadFormListTable from '../../../../Tables/LeadList/FacebookLeadFormListTable';
import { DrawerMenu } from '../../../../Menu';
import LeadFormPreview from '../Leads/LeadFormPreview';

interface LeadFormAssetzProps {
  brand: Brand;
}

const LeadFormAssetz: FC<LeadFormAssetzProps> = ({ brand }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const xsOnly = useMediaQuery(theme.breakpoints.only('xs'));
  const dispatch = useDispatch();
  const [leadForms, setLeadForms] = useState<LeadFormData[]>([]);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [openFormPreview, setOpenFormPreview] = useState<boolean>(false);
  const [selectedLeadForm, setSelectedLeadForm] = useState<LeadFormData>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedForms, setSelectedForms] = useState<FBLeadGenData[]>([]);
  const [openSyncFormModal, setOpenSyncFormModal] = useState<boolean>(false);
  const [archiveLoading, setArchiveLoading] = useState<boolean>(false);

  useEffect(() => {
    if (brand) {
      getLeadForms();
    }
  }, [brand]);

  const getLeadForms = async () => {
    try {
      setFetchLoading(true);

      const response = await getBrandLeadForms(brand?._id);

      const sortedResponse = response.data.sort((leadForm: LeadFormData) => {
        if (leadForm.default) return -1;

        return 1;
      });

      setLeadForms(sortedResponse);
    } catch (error: any) {
      console.log(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSyncForms = async () => {
    try {
      if (selectedForms?.length === 0) {
        return dispatch(
          toggleAlert({
            toggle: true,
            message: 'Please select at least 1 form',
            type: 'error',
          }),
        );
      }

      setLoading(true);

      const response = await syncLeadForms(selectedForms, brand?._id);

      setLeadForms([...leadForms, ...response.data]);

      dispatch(
        toggleAlert({
          toggle: true,
          message: `You successfully sync ${response.count} lead forms`,
        }),
      );

      handleOpenSyncForm();
      navigate(`/assetz/form/set-up/${response.data[0]?._id}`);
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);
      dispatch(
        toggleAlert({
          toggle: true,
          message: errorMsg,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOnArchive = async () => {
    try {
      setArchiveLoading(true);

      const response = await archiveLeadForm(selectedLeadForm?._id);

      let temp: LeadFormData[] = [];
      leadForms.forEach((form: LeadFormData) => {
        if (form._id !== response.data._id) {
          temp = [...temp, form];
        }
      });

      setLeadForms(temp);

      handleCloseFormPreview();
      dispatch(
        toggleAlert({
          toggle: true,
          message: 'You successfully archive the form',
        }),
      );
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(toggleAlert({ toggle: true, message: errorMsg, type: 'error' }));
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleOpenFormPreview = (leadForm: LeadFormData) => {
    setOpenFormPreview(true);
    setSelectedLeadForm(leadForm);
  };

  const handleCloseFormPreview = () => {
    setOpenFormPreview(false);
    setSelectedLeadForm(null);
  };

  const handleOpenSyncForm = () => {
    setOpenSyncFormModal((prev) => !prev);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
          <div>
            <PrimaryButton
              title="Sync Form"
              type="button"
              handleOnClick={handleOpenSyncForm}
              marginRight5
              endIcon={
                <HtmlTooltip
                  disableInteractive
                  title={
                    <Fragment>
                      <Box
                        component="div"
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        Sync Form
                      </Box>

                      <div>
                        Integrate your Lead Generation form created through
                        Facebook Ads Manager.
                      </div>
                    </Fragment>
                  }
                >
                  <InfoTwoToneIcon sx={{ fontSize: '18px' }} />
                </HtmlTooltip>
              }
            />

            {brand?.__type === 'brand' ? (
              <PrimaryButton
                title="Create Form"
                type="button"
                handleOnClick={() => navigate('/assetz/form/create')}
              />
            ) : null}
          </div>
        </Grid>

        <Grid item xs={12}>
          <LeadFormListTable
            leadForms={leadForms}
            loading={fetchLoading}
            onOpenPreview={handleOpenFormPreview}
            isMobile={xsOnly}
          />
        </Grid>
      </Grid>

      <DrawerMenu
        anchor="right"
        open={openFormPreview}
        onClose={handleCloseFormPreview}
      >
        <LeadFormPreview
          leadForm={selectedLeadForm}
          onClose={handleCloseFormPreview}
          onArchive={handleOnArchive}
          loading={archiveLoading}
        />
      </DrawerMenu>

      <PopupModal open={openSyncFormModal} handleClose={handleOpenSyncForm}>
        <Grid container spacing={2} sx={{ width: xsOnly ? '100%' : '600px' }}>
          <Grid item xs={12}>
            <ModalHeader title="Sync Lead Forms" />
          </Grid>

          <Grid item xs={12}>
            <FacebookLeadFormListTable
              brand={brand}
              setSelectedForms={setSelectedForms}
              forms={leadForms}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              position: 'sticky',
              bottom: 0,
              justifyContent: 'center',
              display: 'flex',
            }}
          >
            <PrimaryButton
              title="Sync Forms"
              type="button"
              handleOnClick={handleSyncForms}
              loading={loading}
            />
          </Grid>
        </Grid>
      </PopupModal>
    </Box>
  );
};

export default LeadFormAssetz;
